import React from "react"; // eslint-disable-line no-unused-vars
import {
  Grid2 as Grid,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Select,
  MenuItem,
  FormGroup,
  Checkbox,
  Paper,
  InputLabel,
} from "@mui/material";
import { Donation, CodeDetails, Settings } from "../types";
import { RequiredTextField, OptionalTextField } from "../components";
import {
  getPhoneErrors,
  getNameErrors,
  getStreetErrors,
  getCityErrors,
  getStateErrors,
  getZipErrors,
  getEmailErrors,
  getAttributionErrors,
  getDesignationErrors,
} from "../utilities/validators";

export interface DonationInstructionsProps {
  codeDetails: CodeDetails;
  settings: Settings;
  donation: Donation;
  handleChange: (donation: Donation) => void;
}

export const DonationInstructions = ({
  codeDetails,
  settings,
  donation,
  handleChange,
}: DonationInstructionsProps): React.JSX.Element | null => {
  const updateInstructionType = (
    checked: boolean,
    type: string,
    updatedDonation: Donation
  ) => {
    let instructions = { ...updatedDonation.instructions };
    if (checked) {
      instructions.type.push(type);
    } else {
      let index = instructions.type.indexOf(type);
      instructions.type.splice(index, 1);
    }
    handleChange({
      ...updatedDonation,
      instructions: instructions,
    });
  };

  const renderOptions = () => {
    if (
      (settings.allowedInstructions.indexOf("OptIn") >= 0 &&
        settings.cardValueMinimumOptIn <= codeDetails.originalAmount) ||
      (settings.allowedInstructions.indexOf("Attribution") >= 0 &&
        settings.cardValueMinimumAttribution <= codeDetails.originalAmount) ||
      (settings.allowedInstructions.indexOf("Designation") >= 0 &&
        settings.cardValueMinimumDesignation <= codeDetails.originalAmount) ||
      (settings.allowedInstructions.indexOf("Paper") >= 0 &&
        settings.cardValueMinimumPaper <= codeDetails.originalAmount) ||
      (settings.allowedInstructions.indexOf("Note") >= 0 &&
        settings.cardValueMinimumNote <= codeDetails.originalAmount)
    ) {
      return (
        <Grid size={{ xs: 12 }}>
          <FormControl>
            <FormLabel>
              <Typography
                variant="body1"
                component="div"
                style={{ color: "rgba(0, 0, 0, 0.87)", marginBottom: "8px" }}
              >
                Choose additional instructions for your donation.
              </Typography>
            </FormLabel>
            <FormGroup>
              {settings.allowedInstructions.indexOf("OptIn") >= 0 &&
                settings.cardValueMinimumOptIn <=
                  codeDetails.originalAmount && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          donation.instructions.type.indexOf("OptIn") >= 0
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          let updatedDonation = { ...donation };
                          if (!event.target.checked) {
                            updatedDonation = {
                              ...donation,
                              instructions: {
                                ...donation.instructions,
                                optIn: {
                                  ...donation.instructions.optIn,
                                  accept: false,
                                  email: "",
                                  name: "",
                                },
                              },
                            };
                          } else {
                            updatedDonation = {
                              ...donation,
                              instructions: {
                                ...donation.instructions,
                                optIn: {
                                  ...donation.instructions.optIn,
                                  accept: true,
                                  email: codeDetails.email,
                                  name: codeDetails.name,
                                },
                              },
                            };
                          }
                          updateInstructionType(
                            event.target.checked,
                            "OptIn",
                            updatedDonation
                          );
                        }}
                      />
                    }
                    label="Share my name and email with the charity"
                  />
                )}
              {settings.allowedInstructions.indexOf("Attribution") >= 0 &&
                settings.cardValueMinimumAttribution <=
                  codeDetails.originalAmount && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          donation.instructions.type.indexOf("Attribution") >= 0
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          updateInstructionType(
                            event.target.checked,
                            "Attribution",
                            { ...donation }
                          );
                        }}
                      />
                    }
                    label="Donate in honor of someone"
                  />
                )}
              {settings.allowedInstructions.indexOf("Designation") >= 0 &&
                settings.cardValueMinimumDesignation <=
                  codeDetails.originalAmount && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          donation.instructions.type.indexOf("Designation") >= 0
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          updateInstructionType(
                            event.target.checked,
                            "Designation",
                            { ...donation }
                          );
                        }}
                      />
                    }
                    label="Suggest a particular program"
                  />
                )}
              {settings.allowedInstructions.indexOf("Paper") >= 0 &&
                settings.cardValueMinimumPaper <=
                  codeDetails.originalAmount && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          donation.instructions.type.indexOf("Paper") >= 0
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          updateInstructionType(event.target.checked, "Paper", {
                            ...donation,
                          });
                        }}
                      />
                    }
                    label="Mail check to me to hand-deliver"
                  />
                )}
              {settings.allowedInstructions.indexOf("Note") >= 0 &&
                settings.cardValueMinimumNote <= codeDetails.originalAmount && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          donation.instructions.type.indexOf("Note") >= 0
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          updateInstructionType(event.target.checked, "Note", {
                            ...donation,
                          });
                        }}
                      />
                    }
                    label="Send a note to TisBest"
                  />
                )}
            </FormGroup>
          </FormControl>
        </Grid>
      );
    } else {
      return null;
    }
  };

  const renderOptIn = () => {
    if (
      donation.instructions.type.indexOf("OptIn") >= 0 &&
      settings.cardValueMinimumOptIn <= codeDetails.originalAmount
    ) {
      return (
        <Grid size={{ xs: 12 }} container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              component="div"
              style={{ paddingBottom: "6px", paddingLeft: "4px" }}
            >
              Only the opt-in name and email will be shared with this charity.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              autoComplete="name"
              disabled={false}
              label="Opt In Name"
              value={donation.instructions.optIn.name}
              variant="paper"
              test={(value: string) => {
                return getNameErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    optIn: {
                      ...donation.instructions.optIn,
                      name: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              autoComplete="email"
              disabled={false}
              label="Opt In Email"
              value={donation.instructions.optIn.email}
              variant="paper"
              test={(value: string) => {
                return getEmailErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    optIn: {
                      ...donation.instructions.optIn,
                      email: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return null;
    }
  };

  const renderContact = () => {
    if (
      (donation.instructions.type.indexOf("Attribution") >= 0 &&
        settings.cardValueMinimumAttribution <= codeDetails.originalAmount) ||
      (donation.instructions.type.indexOf("Designation") >= 0 &&
        settings.cardValueMinimumDesignation <= codeDetails.originalAmount) ||
      (donation.instructions.type.indexOf("Paper") >= 0 &&
        settings.cardValueMinimumPaper <= codeDetails.originalAmount) ||
      (donation.instructions.type.indexOf("Note") >= 0 &&
        settings.cardValueMinimumNote <= codeDetails.originalAmount)
    ) {
      return (
        <Grid size={{ xs: 12 }} container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              component="div"
              style={{ paddingBottom: "6px", paddingLeft: "4px" }}
            >
              TisBest may need to ask follow-up questions regarding your
              donation, and will not share your contact information with any
              third parties.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              autoComplete="name"
              disabled={false}
              label="Contact Name"
              value={donation.instructions.contact.name}
              variant="paper"
              test={(value: string) => {
                return getNameErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    contact: {
                      ...donation.instructions.contact,
                      name: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              autoComplete="tel"
              disabled={false}
              label="Contact Phone"
              value={donation.instructions.contact.phone}
              variant="paper"
              test={(value: string) => {
                return getPhoneErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    contact: {
                      ...donation.instructions.contact,
                      phone: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              autoComplete="email"
              disabled={false}
              label="Contact Email"
              value={donation.instructions.contact.email}
              variant="paper"
              test={(value: string) => {
                return getEmailErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    contact: {
                      ...donation.instructions.contact,
                      email: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return null;
    }
  };

  const renderPaperCheck = () => {
    if (
      donation.instructions.type.indexOf("Paper") >= 0 &&
      settings.cardValueMinimumPaper <= codeDetails.originalAmount
    ) {
      return (
        <Grid size={{ xs: 12 }} container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              component="div"
              style={{ paddingBottom: "6px", paddingLeft: "4px" }}
            >
              Please provide the address where you would like the check sent.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              disabled={false}
              label="Name"
              value={donation.instructions.address.name}
              variant="paper"
              test={(value: string) => {
                return getNameErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    address: {
                      ...donation.instructions.address,
                      name: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              autoComplete="address-line1"
              disabled={false}
              label="Street"
              value={donation.instructions.address.street}
              variant="paper"
              test={(value: string) => {
                return getStreetErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    address: {
                      ...donation.instructions.address,
                      street: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <OptionalTextField
              autoComplete="address-line2"
              disabled={false}
              label="Unit"
              value={donation.instructions.address.unit}
              variant="paper"
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    address: {
                      ...donation.instructions.address,
                      unit: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <RequiredTextField
              autoComplete="address-level2"
              disabled={false}
              label="City"
              value={donation.instructions.address.city}
              variant="paper"
              test={(value: string) => {
                return getCityErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    address: {
                      ...donation.instructions.address,
                      city: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              autoComplete="address-level1"
              required={true}
              fullWidth={true}
              InputProps={{
                className: "tb_paper_input",
              }}
              label="State"
              select={true}
              value={donation.instructions.address.state}
              onChange={(event) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    address: {
                      ...donation.instructions.address,
                      state: event.target.value,
                    },
                  },
                });
              }}
            >
              <MenuItem value="AL">Alabama</MenuItem>
              <MenuItem value="AK">Alaska</MenuItem>
              <MenuItem value="AZ">Arizona</MenuItem>
              <MenuItem value="AR">Arkansas</MenuItem>
              <MenuItem value="CA">California</MenuItem>
              <MenuItem value="CO">Colorado</MenuItem>
              <MenuItem value="CT">Connecticut</MenuItem>
              <MenuItem value="DE">Delaware</MenuItem>
              <MenuItem value="FL">Florida</MenuItem>
              <MenuItem value="GA">Georgia</MenuItem>
              <MenuItem value="HI">Hawaii</MenuItem>
              <MenuItem value="ID">Idaho</MenuItem>
              <MenuItem value="IL">Illinois</MenuItem>
              <MenuItem value="IN">Indiana</MenuItem>
              <MenuItem value="IA">Iowa</MenuItem>
              <MenuItem value="KS">Kansas</MenuItem>
              <MenuItem value="KY">Kentucky</MenuItem>
              <MenuItem value="LA">Louisiana</MenuItem>
              <MenuItem value="ME">Maine</MenuItem>
              <MenuItem value="MD">Maryland</MenuItem>
              <MenuItem value="MA">Massachusetts</MenuItem>
              <MenuItem value="MI">Michigan</MenuItem>
              <MenuItem value="MN">Minnesota</MenuItem>
              <MenuItem value="MS">Mississippi</MenuItem>
              <MenuItem value="MO">Missouri</MenuItem>
              <MenuItem value="MT">Montana</MenuItem>
              <MenuItem value="NE">Nebraska</MenuItem>
              <MenuItem value="NV">Nevada</MenuItem>
              <MenuItem value="NH">New Hampshire</MenuItem>
              <MenuItem value="NJ">New Jersey</MenuItem>
              <MenuItem value="NM">New Mexico</MenuItem>
              <MenuItem value="NY">New York</MenuItem>
              <MenuItem value="NC">North Carolina</MenuItem>
              <MenuItem value="ND">North Dakota</MenuItem>
              <MenuItem value="OH">Ohio</MenuItem>
              <MenuItem value="OK">Oklahoma</MenuItem>
              <MenuItem value="OR">Oregon</MenuItem>
              <MenuItem value="PA">Pennsylvania</MenuItem>
              <MenuItem value="RI">Rhode Island</MenuItem>
              <MenuItem value="SC">South Carolina</MenuItem>
              <MenuItem value="SD">South Dakota</MenuItem>
              <MenuItem value="TN">Tennessee</MenuItem>
              <MenuItem value="TX">Texas</MenuItem>
              <MenuItem value="UT">Utah</MenuItem>
              <MenuItem value="VT">Vermont</MenuItem>
              <MenuItem value="VA">Virginia</MenuItem>
              <MenuItem value="WA">Washington</MenuItem>
              <MenuItem value="WV">West Virginia</MenuItem>
              <MenuItem value="WI">Wisconsin</MenuItem>
              <MenuItem value="WY">Wyoming</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <RequiredTextField
              autoComplete="postal-code"
              disabled={false}
              label="Zip"
              value={donation.instructions.address.zip}
              variant="paper"
              test={(value: string) => {
                return getZipErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    address: {
                      ...donation.instructions.address,
                      zip: event.target.value,
                    },
                  },
                });
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return null;
    }
  };

  const renderAttribution = () => {
    if (
      donation.instructions.type.indexOf("Attribution") >= 0 &&
      settings.cardValueMinimumAttribution <= codeDetails.originalAmount
    ) {
      return (
        <Grid size={{ xs: 12 }} container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              component="div"
              style={{ paddingBottom: "6px", paddingLeft: "4px" }}
            >
              Provide the name of the person you would like this donation to
              honor.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              disabled={false}
              label="Name to Donate in Honor of"
              value={donation.instructions.attribution}
              variant="paper"
              test={(value: string) => {
                return getAttributionErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    attribution: event.target.value,
                  },
                });
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return null;
    }
  };

  const renderDesignation = () => {
    if (
      donation.instructions.type.indexOf("Designation") >= 0 &&
      settings.cardValueMinimumDesignation <= codeDetails.originalAmount
    ) {
      return (
        <Grid size={{ xs: 12 }} container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              component="div"
              style={{ paddingBottom: "6px", paddingLeft: "4px" }}
            >
              Suggest a designation or program to which you would like these
              funds directed.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <RequiredTextField
              disabled={false}
              label="Designation Request"
              value={donation.instructions.designation}
              variant="paper"
              test={(value: string) => {
                return getDesignationErrors(value);
              }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    designation: event.target.value,
                  },
                });
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return null;
    }
  };

  const renderNotes = () => {
    if (
      (donation.instructions.type.indexOf("Attribution") >= 0 &&
        settings.cardValueMinimumAttribution <= codeDetails.originalAmount) ||
      (donation.instructions.type.indexOf("Designation") >= 0 &&
        settings.cardValueMinimumDesignation <= codeDetails.originalAmount) ||
      (donation.instructions.type.indexOf("Paper") >= 0 &&
        settings.cardValueMinimumPaper <= codeDetails.originalAmount) ||
      (donation.instructions.type.indexOf("Note") >= 0 &&
        settings.cardValueMinimumNote <= codeDetails.originalAmount)
    ) {
      return (
        <Grid size={{ xs: 12 }} container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              component="div"
              style={{ paddingBottom: "6px", paddingLeft: "4px" }}
            >
              Provide any extra instructions, notes, or questions for the
              TisBest team.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Instructions Notes"
              fullWidth={true}
              multiline={true}
              rows={5}
              value={donation.instructions.notes}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                handleChange({
                  ...donation,
                  instructions: {
                    ...donation.instructions,
                    notes: event.target.value,
                  },
                });
              }}
            />
          </Grid>
        </Grid>
      );
    } else {
      return null;
    }
  };

  if (settings && settings.allowedInstructions.length > 0) {
    return (
      <Grid size={{ xs: 12 }} container spacing={2}>
        {renderOptions()}
        {donation.instructions.type.length > 0 && (
          <Grid size={{ xs: 12 }} container spacing={1}>
            {renderOptIn()}
            {renderContact()}
            {renderPaperCheck()}
            {renderAttribution()}
            {renderDesignation()}
            {renderNotes()}
          </Grid>
        )}
      </Grid>
    );
  } else {
    return null;
  }
};
